import type { ScoreState } from '../../sdk/scoring';
import type { RewardResult } from '../../shared/types/rewards';
import type { CompletedGameSession, SessionRecord } from '../../shared/types/session';
import type { MissionAnalyticsWriter } from './analytics-writer';

/**
 * Turns a finished mission into the object the parent summary is built from.
 *
 * The non-competitive invariant lives here rather than in the screen that renders it: there is
 * one shared team score, participation is a neutral count per player, and no field in the shape
 * can express "first", "fastest" or "better" (architecture §12, MVP notes §21).
 *
 * Metric names follow D-007 — *questions attempted* and *questions completed*, not "score" and
 * "accuracy".
 */

export interface AssembleCompletedSessionInput {
  record: SessionRecord;
  score: ScoreState;
  analytics: MissionAnalyticsWriter;
  startedAtMs: number;
  completedAtMs: number;
  /** ISO-8601 instant the mission finished. */
  completedAt: string;
  /**
   * The reward the child was shown. Carried on the session so the game's progression provider
   * persists the same badge rather than deriving a second opinion (D-038(6)).
   */
  reward?: RewardResult;
  /**
   * The sitting was ended by a grown-up before the mission finished (game design spec §32).
   * Carried onto the session so the parent summary and the progression provider can tell a
   * closed sitting from a finished mission — `completedAt` cannot, because it is true of both.
   */
  endedEarly?: boolean;
}

export function assembleCompletedSession(
  input: AssembleCompletedSessionInput,
): CompletedGameSession {
  const record: SessionRecord = { ...input.record, completedAt: input.completedAt };

  return {
    record,
    summary: {
      sessionId: record.sessionId,
      gameId: record.gameId,
      missionId: record.missionId,
      playerMode: record.playerMode,
      // Read from the writer, not from this sitting's outcomes: a resumed mission carries the
      // counters the earlier sitting earned (PRO-03).
      questionsAttempted: input.analytics.questionsAttempted(),
      questionsCompleted: input.analytics.questionsCompleted(),
      retryCount: input.analytics.retryCount(),
      teachingMomentCount: input.analytics.teachingMomentCount(),
      teamScore: input.score.teamScore,
      // A clock that has gone backwards (a device sleeping across a time change) must not produce
      // a negative session length in a parent's summary.
      durationMs: Math.max(0, input.completedAtMs - input.startedAtMs),
      participation: input.analytics.participation(),
      completedAt: input.completedAt,
    },
    evidence: input.analytics.evidence(),
    // The whole session's practice, not just this sitting's evidence: an interrupted mission
    // carried its counters across but not the skills behind them (R1-10, GLP-08).
    skillPractice: input.analytics.skillPractice(),
    ...(input.reward === undefined ? {} : { reward: input.reward }),
    ...(input.endedEarly === true ? { endedEarly: true } : {}),
  };
}
